import { useEffect } from 'react';
import { useLocation } from 'react-router';
import {
  getPageMetadata,
  getArticleStructuredData,
  getSocialImageMetadata,
} from '@/routes';
import {
  getRobotsContent,
  getStructuredData,
  serializeStructuredData,
  STRUCTURED_DATA_ID,
} from '@/lib/seo';

/** Keep the build-time metadata current during client-side navigation. */
export function PageMetadata() {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = getPageMetadata(pathname);
    const canonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    const origin = canonical
      ? new URL(canonical.href).origin
      : window.location.origin;
    document.title = page.title;
    if (canonical) canonical.href = origin + page.canonicalPath;
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = getRobotsContent(page, origin);
    const graph = getStructuredData(page, origin);
    let pageData = document.getElementById(STRUCTURED_DATA_ID);
    if (graph) {
      if (!pageData) {
        pageData = document.createElement('script');
        pageData.id = STRUCTURED_DATA_ID;
        pageData.setAttribute('type', 'application/ld+json');
        document.head.appendChild(pageData);
      }
      pageData.textContent = serializeStructuredData(graph);
    } else pageData?.remove();
    for (const [key, value] of Object.entries({
      description: page.description,
      'og:title': page.title,
      'og:description': page.description,
      'og:url': origin + page.canonicalPath,
      'og:type': page.article ? 'article' : 'website',
      'twitter:title': page.title,
      'twitter:description': page.description,
      ...getSocialImageMetadata(page, origin),
    })) {
      let meta = document.querySelector<HTMLMetaElement>(
        `meta[name="${key}"],meta[property="${key}"]`
      );
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(key.startsWith('og:') ? 'property' : 'name', key);
        document.head.appendChild(meta);
      }
      meta.content = value;
    }
    for (const [key, value] of Object.entries({
      'article:published_time': page.article?.datePublished,
      'article:modified_time': page.article?.dateModified,
    })) {
      let meta = document.querySelector<HTMLMetaElement>(
        `meta[property="${key}"]`
      );
      if (value) {
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', key);
          document.head.appendChild(meta);
        }
        meta.content = value;
      } else meta?.remove();
    }
    let structuredData = document.getElementById('article-structured-data');
    if (page.article) {
      if (!structuredData) {
        structuredData = document.createElement('script');
        structuredData.id = 'article-structured-data';
        structuredData.setAttribute('type', 'application/ld+json');
        document.head.appendChild(structuredData);
      }
      structuredData.textContent = serializeStructuredData(
        getArticleStructuredData(page, origin)
      );
    } else structuredData?.remove();
  }, [pathname]);
  return null;
}
